import type { SupabaseClient } from "@supabase/supabase-js";

// Effective site policy read model (CONSOLE-POLICY-READ-SURFACE). One read-only RPC -
// public.effective_site_policy(p_account_id, p_site_id) from Codex's D1 policy-override
// foundation (20260611102842). The resolver is the single source of truth for the
// override -> account -> default cascade; the console NEVER merges policy layers itself.
// Write helpers below stay on the hosted RPC contracts; no direct table writes live here.

export type PolicySource = "site_override" | "account_policy" | "registry_default";

export interface EffectivePolicyRow {
  key: string;
  valueType: string;
  scope: string;
  effectiveValue: unknown;
  source: PolicySource;
}

/** Source chips use the resolver's exact vocabulary; unknown sources fail to "Default". */
export const SOURCE_COPY: Record<PolicySource, string> = {
  site_override: "Site override",
  account_policy: "Account",
  registry_default: "Default",
};

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asSource(value: unknown): PolicySource {
  return value === "site_override" || value === "account_policy" ? value : "registry_default";
}

/** Resolve every policy key's effective value for one site. Throws safe. */
export async function fetchEffectiveSitePolicy(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
): Promise<EffectivePolicyRow[]> {
  const { data, error } = await client.rpc("effective_site_policy", {
    p_account_id: accountId,
    p_site_id: siteId,
  });
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load site policies.");
  }
  const rows: EffectivePolicyRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const key = asString(raw.key);
    if (key === null) {
      continue;
    }
    rows.push({
      key,
      valueType: asString(raw.value_type) ?? "unknown",
      scope: asString(raw.scope) ?? "account_only",
      effectiveValue: raw.effective_value ?? null,
      source: asSource(raw.source),
    });
  }
  return rows;
}

// --- Writes (CONSOLE-POLICY-OVERRIDE-EDITOR) ----------------------------------------------
// Four hosted RPCs (20260611111140), policy.manage-gated server-side. Direct table writes
// are revoked - these are the ONLY write paths. After any success the caller refetches the
// resolver; the console never merges policy layers itself.

export const SAFE_POLICY_WRITE_FAILURE = "Couldn't save that policy change - please try again.";

// Static contract sentences render verbatim (exact match)...
const KNOWN_POLICY_WRITE_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage policies for this account.",
  "Policy key is required.",
  "Policy value is required.",
  "Site was not found for this account.",
  "Account and site ids are required to manage site policies.",
  "Branding is not enabled for this workspace.",
]);

// ...and the foundation's key-templated validation sentences match via CONSTRAINED
// patterns - the interpolations are registry key names / value types (public vocabulary,
// never secrets). Anything else fails closed.
const KNOWN_POLICY_WRITE_PATTERNS: ReadonlyArray<RegExp> = [
  /^Policy key [a-z0-9_.]+ is account-only and cannot be overridden per site\.$/,
  /^Policy key [a-z0-9_.]+ expects value_type [a-z]+\.$/,
  /^Unknown policy key: [a-z0-9_.]+$/,
  /^Policy value for [a-z0-9_.]+ looks secret-shaped\.$/,
];

export type PolicyWriteOutcome = { ok: true } | { ok: false; reason: string };

async function runPolicyWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<PolicyWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    if (error === null) {
      return { ok: true };
    }
    const message = typeof error.message === "string" ? error.message : "";
    if (KNOWN_POLICY_WRITE_FAILURES.has(message) || KNOWN_POLICY_WRITE_PATTERNS.some((p) => p.test(message))) {
      return { ok: false, reason: message };
    }
    return { ok: false, reason: SAFE_POLICY_WRITE_FAILURE };
  } catch {
    return { ok: false, reason: SAFE_POLICY_WRITE_FAILURE };
  }
}

/** Set the account-wide value for a key. Never throws. */
export async function setAccountPolicy(
  client: SupabaseClient,
  accountId: string,
  key: string,
  value: unknown,
): Promise<PolicyWriteOutcome> {
  return runPolicyWrite(client, "set_account_policy", { p_account_id: accountId, p_key: key, p_value: value });
}

/** Clear the account-wide value (sites fall back to the shipped default). Never throws. */
export async function clearAccountPolicy(
  client: SupabaseClient,
  accountId: string,
  key: string,
): Promise<PolicyWriteOutcome> {
  return runPolicyWrite(client, "clear_account_policy", { p_account_id: accountId, p_key: key });
}

/** Set a site-specific override (site_overridable keys only - server enforces). Never throws. */
export async function setSitePolicyOverride(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  key: string,
  value: unknown,
): Promise<PolicyWriteOutcome> {
  return runPolicyWrite(client, "set_site_policy_override", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_key: key,
    p_value: value,
  });
}

/** Remove a site override (the site inherits account value or default). Never throws. */
export async function clearSitePolicyOverride(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  key: string,
): Promise<PolicyWriteOutcome> {
  return runPolicyWrite(client, "clear_site_policy_override", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_key: key,
  });
}

/** Group keys by their dotted prefix ("capture.scan_cooldown_seconds" -> "capture"). */
export function policyDomain(key: string): string {
  const dot = key.indexOf(".");
  return dot === -1 ? key : key.slice(0, dot);
}

export type ParsedPolicyInput = { ok: true; value: unknown } | { ok: false; message: string };

/** Parse a typed editor input by the registry's value_type. Inline messages, no writes. */
export function parsePolicyInput(valueType: string, raw: string): ParsedPolicyInput {
  if (valueType === "integer") {
    const value = Number(raw);
    return Number.isInteger(value) && raw.trim() !== ""
      ? { ok: true, value }
      : { ok: false, message: "Enter a whole number." };
  }
  if (valueType === "boolean") {
    if (raw === "true") return { ok: true, value: true };
    if (raw === "false") return { ok: true, value: false };
    return { ok: false, message: "Choose true or false." };
  }
  if (valueType === "string") {
    return { ok: true, value: raw };
  }
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false, message: "Enter valid JSON." };
  }
}

/** Compact, human value rendering - never raw JSON editors, never undefined soup. */
export function formatPolicyValue(value: unknown): string {
  if (value === null || value === undefined) return "not set";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}
